import { Project } from "@/lib/types";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Edit, Trash2, Eye, FolderOpen } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { deleteProject } from "@/services/project-service";
import { toast } from "sonner";
import { CreateProjectDialog } from "./CreateProjectDialog";
import { useTranslation } from "react-i18next";

interface ProjectCardProps {
  project: Project & { preview_count: number };
  onProjectDeleted: (projectId: string) => void;
  onProjectUpdated: (updatedProject: Project) => void;
}

export function ProjectCard({ project, onProjectDeleted, onProjectUpdated }: ProjectCardProps) {
  const handleDelete = async () => {
    try {
      const result = await deleteProject(project.id);
      if (result.success) {
        toast.success(t("common:projectCard.toast.success"));
        onProjectDeleted(project.id);
      } else {
        throw new Error(result.error || "Failed to delete project.");
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("common:projectCard.toast.error")
      );
      console.error("Delete project error:", error);
    }
  };
  const { t } = useTranslation("common");

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <CardTitle className="line-clamp-2">{project.name}</CardTitle>
        <CardDescription className="text-xs text-muted-foreground">
          {t("common:projectCard.lastUpdated")}:{" "}
          {new Date(project.updated_at).toLocaleDateString()}
        </CardDescription>
      </CardHeader>{" "}
      <CardContent className="flex-grow">
        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
          {project.description || "No description provided."}
        </p>
        
        {/* Preview count badge */}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="flex items-center gap-1">
            <FolderOpen className="h-3 w-3" />
            {project.preview_count} {t("common:projectCard.preview")}
            {project.preview_count !== 1 ? "s" : ""}
          </Badge>
          {project.is_archived && (
            <Badge variant="secondary">
              {t("common:projectCard.archived")}
            </Badge>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between items-center">
        {project.preview_count === 0 ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/models">
              <Eye className="mr-2 h-4 w-4" />
              {t("common:projectCard.create")}
            </Link>
          </Button>
        ) : (
          <Button asChild variant="outline" size="sm">
            <Link to={`/projects/${project.id}`}>
              <Eye className="mr-2 h-4 w-4" />
              {t("common:projectCard.viewPreviews")} ({project.preview_count})
            </Link>
          </Button>
        )}{" "}
        <div className="flex space-x-2">
          <CreateProjectDialog
            onProjectCreated={onProjectUpdated}
            project={project}
            mode="edit"
            trigger={
              <Button variant="ghost" size="icon" aria-label="Edit project">
                <Edit className="h-4 w-4" />
              </Button>
            }
          />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Delete project"
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("common:projectCard.delete.sure")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t("common:projectCard.delete.note", {
                    project: { name: project.name },
                  })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  {" "}
                  {t("common:buttons.cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  {t("common:buttons.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardFooter>
    </Card>
  );
}
